import { offlineDB } from './offlineDB';
import { RequestVisitService } from '@/lib/collections/VisitsLogs/FetchVisitLogs';
import { toast } from 'react-toastify';
import { checkRealConnectivity } from './connectivity';

class SyncService {
  private isSyncing = false;
  // Control simple de backoff para evitar bombardear al servidor
  private lastSyncAttemptAt: number | null = null;
  private MIN_SYNC_INTERVAL_MS = 5 * 1000; // 5 segundos entre syncAll automáticos

  // Toasts deduplicados
  private inProgressToastId: string | null = null;

  async isOnline(): Promise<boolean> {
    return await checkRealConnectivity();
  }

  //  CONVERTIR dataURL a Blob correctamente
  private async dataURLtoBlob(dataURL: string): Promise<Blob> {
    // Verificar que sea un data URL válido
    if (!dataURL.startsWith('data:')) {
      throw new Error('dataURL inválido');
    }

    const res = await fetch(dataURL);
    const blob = await res.blob();

    // Verificar que sea PNG
    if (blob.type !== 'image/png') {
      console.warn(`Tipo de blob inesperado: ${blob.type}, convirtiendo a PNG`);
      // Forzar tipo PNG
      return new Blob([blob], { type: 'image/png' });
    }

    return blob;
  }

  // Sincronizar un log específico
  async syncLog(visitId: string): Promise<boolean> {
    try {
      const log = await offlineDB.getLog(visitId);
      if (!log) {
        console.warn(` Log ${visitId} no encontrado`);
        return false;
      }

      console.log(`\n Sincronizando: ${visitId}`);
      console.log(' Datos:', {
        visitId: log.visitId,
        contactPerson: log.contactPerson,
        tieneFirmaNueva: log.signatureDataURL && log.signatureDataURL.length > 100,
      });

      await offlineDB.updateLogStatus(visitId, 'syncing');

      // PREPARAR SOLO LOS CAMPOS QUE SE VAN A ACTUALIZAR
      const logData: any = {
        contactPerson: log.contactPerson,
        contactRole: log.contactRole,
        agreements: log.agreements,
        observations: log.observations,
      };

      //  SOLO INCLUIR signature SI HAY FIRMA NUEVA
      if (log.signatureDataURL && log.signatureDataURL.length > 100) {
        console.log(' Subiendo firma nueva...');

        const signatureBlob = await this.dataURLtoBlob(log.signatureDataURL);
        const signatureFileId = await RequestVisitService.uploadSignature(signatureBlob);

        console.log(' Firma subida:', signatureFileId);

        //  INCLUIR signature en logData
        logData.signature = [
          signatureFileId,
          log.location ? `${log.location.lat},${log.location.lng}` : '',
          new Date(log.timestamp).toISOString()
        ];
      } else {
        console.log('ℹSin firma nueva, solo actualizando texto');
        //  NO incluir signature en logData
        // Así Appwrite NO toca el campo signature existente
      }

      console.log(' Actualizando en Appwrite:', Object.keys(logData));
      await RequestVisitService.createVisitLog(visitId, logData);
      console.log(' Actualizado correctamente');

      await offlineDB.removeLog(visitId);
      console.log(' Eliminado de IndexedDB');

      return true;
    } catch (error: any) {
      console.error(` Error sincronizando ${visitId}:`, error);
      await offlineDB.updateLogStatus(visitId, 'failed', error.message);
      return false;
    }
  }

  // Sincronizar todos los logs pendientes
  async syncAll(): Promise<{ success: number; failed: number }> {
    const now = Date.now();

    // Pequeño backoff para llamadas muy seguidas (auto-sync + manual)
    if (this.lastSyncAttemptAt && now - this.lastSyncAttemptAt < this.MIN_SYNC_INTERVAL_MS) {
      // Silencioso: no mostramos toast nuevo para no saturar
      return { success: 0, failed: 0 };
    }

    this.lastSyncAttemptAt = now;

    if (this.isSyncing) {
      if (this.inProgressToastId && !toast.isActive(this.inProgressToastId)) {
        this.inProgressToastId = null;
      }

      // Evitar múltiples toasts por el mismo evento
      if (!this.inProgressToastId) {
        this.inProgressToastId = toast.warning('Ya hay una sincronización en progreso', {
          toastId: 'sync-in-progress',
        }) as any;
      }
      return { success: 0, failed: 0 };
    }

    if (!(await this.isOnline())) {
      toast.error('Sin conexión a internet. No se puede sincronizar ahora.');
      return { success: 0, failed: 0 };
    }

    this.isSyncing = true;
    let toastId: any = null;

    try {
      const pendingLogs = await offlineDB.getPendingLogs();

      if (pendingLogs.length === 0) {
        // Mensaje informativo pero breve
        toast.info('No hay bitácoras pendientes de sincronizar');
        return { success: 0, failed: 0 };
      }

      for (const log of pendingLogs) {
        if (log.status === 'failed') {
          await offlineDB.updateLogStatus(log.visitId, 'pending');
        }
      }

      toastId = toast.loading(`Sincronizando ${pendingLogs.length} bitácora(s)...`, {
        toastId: 'sync-loading',
      });
      this.inProgressToastId = toastId;

      let success = 0;
      let failed = 0;

      for (const log of pendingLogs) {
        console.log(`\n Sincronizando bitácora: ${log.visitId}`);
        const result = await this.syncLog(log.visitId);
        if (result) {
          success++;
          console.log(`Bitácora ${log.visitId} sincronizada`);
        } else {
          failed++;
          console.log(` Bitácora ${log.visitId} falló`);
        }
      }

      // Mostrar resultado
      if (toastId) toast.dismiss(toastId);
      this.inProgressToastId = null;

      if (failed === 0) {
        toast.success(`${success} bitácora(s) sincronizada(s) exitosamente`);
      } else {
        toast.warning(
          ` ${success} sincronizada(s), ${failed} fallida(s). Revisa tu conexión e intenta de nuevo.`
        );
      }

      return { success, failed };
    } catch (error: any) {
      if (toastId) toast.dismiss(toastId);
      this.inProgressToastId = null;
      toast.error('Error durante la sincronización');
      console.error('Error en syncAll:', error);
      return { success: 0, failed: 0 };
    } finally {
      this.isSyncing = false;
    }
  }

  // Auto-sync cuando vuelve la conexión
  startAutoSync() {
    window.addEventListener('online', async () => {
      console.log('Conexión restaurada, iniciando auto-sync...');
      const count = await offlineDB.countPending();

      if (count > 0) {
        toast.info(`Conexión restaurada. ${count} bitácora(s) pendiente(s)`);
        // Esperar 2 segundos antes de sincronizar.
        // La lógica de backoff en syncAll se encargará de evitar llamadas excesivas.
        setTimeout(() => {
          this.syncAll();
        }, 2000);
      }
    });

    window.addEventListener('offline', () => {
      console.log(' Sin conexión - modo offline activado');
      toast.info('Sin conexión. Las bitácoras se guardarán localmente.', {
        autoClose: 3000,
      });
    });
  }
}

export const syncService = new SyncService();